import { useState, useEffect, useRef } from "react";

/**
 * LastFmNowPlaying
 *
 * Props:
 *   username      (string, required) – Last.fm username
 *   apiKey        (string, required) – Last.fm API key
 *   limit         (number, default 1) – how many recent tracks to fetch
 *   pollInterval  (ms, default 60000) – refresh frequency; 0/false disables polling
 *   showAlbumArt  (bool, default true)
 *   className     (string) – extra CSS classes
 */
export default function LastFmNowPlaying({
  username,
  apiKey,
  limit = 1,
  pollInterval = 35000,
  showAlbumArt = true,
  className = "",
}) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    track: null,
    isNowPlaying: false,
  });

  const timerRef = useRef(null);

  useEffect(() => {
    if (!username || !apiKey) {
      setState((s) => ({
        ...s,
        loading: false,
        error: "Missing username or apiKey",
      }));
      return;
    }

    const fetchData = async () => {
      try {
        const data = await fetchLastFmRecentTracks({
          username,
          apiKey,
          limit,
        });

        const trackObj = extractFirstTrack(data);
        if (!trackObj) {
          setState({
            loading: false,
            error: null,
            track: null,
            isNowPlaying: false,
          });
          return;
        }

        const {
          name = "",
          url = "#",
          artist = {},
          image = [],
          album = {},
          ["@attr"]: attr = {},
        } = trackObj;

        const artistName = artist?.["#text"] ?? "";
        const albumName = album?.["#text"] ?? "";
        const artUrl = getBestImage(image);
        const isNow = attr?.nowplaying === "true";

        setState({
          loading: false,
          error: null,
          track: {
            name,
            url,
            artist: artistName,
            album: albumName,
            artUrl,
          },
          isNowPlaying: isNow,
        });
      } catch (err) {
        setState({
          loading: false,
          error: err?.message || "Failed to load Last.fm data",
          track: null,
          isNowPlaying: false,
        });
      }
    };

    fetchData();

    if (pollInterval && pollInterval > 0) {
      timerRef.current = setInterval(fetchData, pollInterval);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [username, apiKey, limit, pollInterval]);

  if (state.loading) {
    return (
      <div className={`lfm-nowplaying ${className}`.trim()}>
        <span>Loading…</span>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`lfm-nowplaying ${className}`.trim()}>
        <span role="alert">Error: {state.error}</span>
      </div>
    );
  }

  if (!state.track) {
    return (
      <div className={`lfm-nowplaying ${className}`.trim()}>
        <span>No recent tracks.</span>
      </div>
    );
  }

  const { name, url, artist, album, artUrl } = state.track;
  const label = state.isNowPlaying ? "Currently listening to" : "Last song played";

  return (
   
    <div className={`lfm-nowplaying ${className}`.trim()}>
    <span className="lfm-label">{label}: </span>
      
      <br></br>
      <span className="lfm-track">
        <a href={url} target="_blank" rel="noopener noreferrer">
          {name}
        </a>{" "}
        – {artist}
        
      </span>
      
    </div>
    
  );
}

/* ---------------- Helper Functions ---------------- */

async function fetchLastFmRecentTracks({ username, apiKey, limit = 1 }) {
  const base = "https://ws.audioscrobbler.com/2.0/";
  const params = new URLSearchParams({
    method: "user.getrecenttracks",
    user: username,
    api_key: apiKey,
    limit: String(limit),
    format: "json",
  });
  const url = `${base}?${params.toString()}`;

  // Try regular fetch first.
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    // Fallback to JSONP if CORS blocked.
    return jsonp(url);
  }
}

// JSONP fallback
function jsonp(url) {
  return new Promise((resolve, reject) => {
    const cbName = "lfm_cb_" + Math.random().toString(36).slice(2);
    const script = document.createElement("script");
    const sep = url.includes("?") ? "&" : "?";
    script.src = `${url}${sep}callback=${cbName}`;
    script.async = true;

    window[cbName] = (data) => {
      cleanup();
      resolve(data);
    };

    script.onerror = (err) => {
      cleanup();
      reject(err instanceof Error ? err : new Error("JSONP load error"));
    };

    function cleanup() {
      delete window[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    document.body.appendChild(script);
  });
}

function extractFirstTrack(apiData) {
  if (!apiData?.recenttracks?.track) return null;
  const t = apiData.recenttracks.track;
  return Array.isArray(t) ? t[0] : t;
}

function getBestImage(imageArr) {
  if (!Array.isArray(imageArr) || imageArr.length === 0) return null;
  // Prefer largest with a non-empty URL
  const withUrls = imageArr.filter((i) => i?.["#text"]);
  if (withUrls.length === 0) return null;
  // Sizes often: small, medium, large, extralarge, mega
  const sizeOrder = ["mega", "extralarge", "large", "medium", "small"];
  for (const size of sizeOrder) {
    const found = withUrls.find((i) => i.size === size && i["#text"]);
    if (found) return found["#text"];
  }
  return withUrls[withUrls.length - 1]["#text"]; // fallback
}

/* ---------------- Optional Basic Styles ---------------- */
/* You can remove or override these in your app's CSS */
export const lastFmNowPlayingStyles = `
.lfm-nowplaying {
 
  align-items: center;
  gap: 0.25rem;
  
}
.lfm-nowplaying .lfm-label {
  font-weight: 600;
}
.lfm-nowplaying .lfm-track a {
  text-decoration: underline;
  color:rgb(133, 131, 131);
}
.lfm-nowplaying .lfm-artwrap {
  margin-left: 0.5rem;
  display: inline-block;
}
.lfm-nowplaying img.lfm-art {
  width: 33%;
  height: auto;
  margin-top:0.4vh;
  object-fit: cover;
  border-radius: 4px;
}
`;

/**
 * Utility component to inject the basic styles above into <head>.
 * Use it once near the root of your app if you want the default styling.
 * check
 */
export function LastFmNowPlayingStyles() {
  return <style dangerouslySetInnerHTML={{ __html: lastFmNowPlayingStyles }} />;
}
